// Composer Studio — 캔버스 뷰포트(줌/패닝/recenter) 공통 컴포넌트.
//
// 양식 편집(form) 탭과 미리보기(preview) 탭이 동일한 줌/패닝/위치초기화 동작을 공유하도록,
// EditorPage 본체에 흩어져 있던 다음을 한 컴포넌트로 추출한다(동작 보존):
//   - 줌(state + Ctrl+휠/Ctrl+=/-/0 키보드 + 슬라이더 + 앵커 보정)
//   - 패닝(스페이스+드래그 / 중간버튼 드래그 → 스크롤 컨테이너 이동)
//   - 오버스크롤 여백(55vh/55vw) + recenter(중앙 정렬) — 100% 줌에서도 패닝 가능
//   - scaler(transform:scale) + baseSize(ResizeObserver) + 캔버스 박스 sizing(base*zoom)
//   - 우하단 줌 컨트롤(−/슬라이더/+/100%/위치초기화)
//
// 콘텐츠(RenderTemplate 등)는 scaler 안의 `children`으로 받고, 편집 오버레이는 박스(미스케일)
// 직속에 두기 위해 `overlays(canvas, zoom)` render-prop으로 주입한다(getBoundingClientRect 기반
// 좌표가 스케일과 무관하게 그대로 화면 위치와 일치 — ResizeHandles/SelectionOverlay 패턴).
// 줌/팬 상태는 인스턴스(탭) 단위로 독립이라, 같은 컴포넌트를 두 탭에서 쓰면 위치가 서로 분리된다.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import { parseCellRef, type CellRef } from './computeCellRange';

/** A4 캔버스 줌 범위(25%~300%). */
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3;
/** A4 세로 콘텐츠 원본 폭(px, pageWidth 기본값) — RenderTemplate 폭(pagePx)과 동일. 박스 폭을 측정
 *  없이 결정해 scaler 측정과의 순환 의존(박스 폭→scaler 측정→박스 폭)을 끊는다. landscape 등은
 *  호출부가 pageWidth prop으로 원본 폭을 전달한다. */
const A4_BASE_WIDTH = 794;

export interface CanvasViewportProps {
  /** scaler(transform:scale) 안에 렌더할 콘텐츠 — RenderTemplate(+form: SelectionStyles/SpacerHints). */
  children: ReactNode;
  /**
   * A4 박스(미스케일) 직속, scaler 뒤에 렌더할 편집 오버레이. canvasEl/zoom을 받아 좌표를 측정한다.
   * (미리보기처럼 오버레이가 없으면 생략 — 줌/팬만 제공.)
   */
  overlays?: (canvas: HTMLDivElement, zoom: number) => ReactNode;
  /** A4 박스 노드를 감싸는 래퍼(form: DndContext). 미지정 시 그대로 렌더. */
  wrapCanvas?: (box: ReactNode) => ReactNode;
  /** A4 박스 클릭/더블클릭 핸들러(form 편집용 — 노드/셀 선택, 인라인 편집 진입). */
  onCanvasClick?: MouseEventHandler<HTMLDivElement>;
  onCanvasDoubleClick?: MouseEventHandler<HTMLDivElement>;
  /** A4 박스 우클릭 — form: 컨텍스트 메뉴(선택 보정 + 추가/복사/붙여넣기/복제/삭제). */
  onCanvasContextMenu?: MouseEventHandler<HTMLDivElement>;
  /** A4 박스 마우스 이동/이탈 핸들러(계산 입력 픽 모드 호버 미리보기용 — 픽 비활성 시 즉시 반환). */
  onCanvasMouseMove?: MouseEventHandler<HTMLDivElement>;
  onCanvasMouseLeave?: MouseEventHandler<HTMLDivElement>;
  /** 캔버스 박스 바깥(회색 배경) 클릭 — form: 선택 해제. */
  onOutsideClick?: () => void;
  /**
   * zoom 외에 이 값이 바뀌면 한 프레임 뒤 재렌더해 오버레이가 갱신된 레이아웃으로 재측정하게 한다.
   * form은 template을 넘긴다(노드 추가/삭제/리사이즈로 DOM이 바뀌면 오버레이 rect 재측정 필요).
   */
  remeasureDep?: unknown;
  /**
   * 셀 드래그 범위 선택(엑셀/HWP식) — 셀 위 plain mousedown 후 threshold(약 4px) 초과 이동 시
   * 시작된다. onCellDragStart로 anchor 셀을 단일 선택하고, 이동 중 onCellDragMove로 anchor~target
   * 직사각 범위를 갱신한다(범위 계산은 호출부=EditorPage가 computeCellRange로 수행).
   * 패닝(armed/panning)/인라인 편집 textarea 내부/노드 드래그 strip과 간섭하지 않도록 가드된다.
   * (미리보기 탭처럼 미지정 시 셀 드래그 선택 비활성.)
   */
  onCellDragStart?: (anchor: CellRef) => void;
  onCellDragMove?: (anchor: CellRef, target: CellRef) => void;
  /**
   * 캔버스 박스/스케일러 폭(px, 미스케일 원본). page(size+orientation)에서 산출한 값(landscape면
   * A4 297mm 폭≈1123px). 미지정 시 A4 세로 폭(794px). RenderTemplate의 페이지 폭과 반드시 일치해야
   * 박스가 콘텐츠를 클립하지 않는다.
   */
  pageWidth?: number;
  /**
   * 뷰포트(줌/패닝) 유지 키 — 지정 시 localStorage(`composer-viewport:<key>`)에 줌·스크롤을
   * debounce 저장하고, 캔버스 (재)마운트 때 recenter 대신 복원한다(새로고침·탭 복귀 위치 유지).
   * 템플릿별로 구분되게 template.key를 포함할 것(예: `edit:${template.key}`). 미지정 시 기존
   * 중앙 정렬 동작 그대로. 저장 스크롤은 창 크기(오버스크롤 여백 55vw/vh)에 의존하므로 창이
   * 크게 바뀌면 어긋날 수 있음 — 탈출구는 위치 초기화(recenter) 버튼.
   */
  persistKey?: string;
  /**
   * 마운트 시 + `pageWidth` 변경 시, 캔버스 전체가 스크롤 컨테이너 폭에 들어오도록 줌을 자동 설정
   * (fit-to-width) 후 중앙 정렬한다. 상한은 1(원본보다 크게는 확대하지 않음). 이후 사용자는 Ctrl+휠 등으로
   * 자유롭게 줌할 수 있고, 다음 `pageWidth` 변경 때 다시 fit된다. 미지정(기본 false) 시 줌 1로 시작하는 기존
   * 동작 그대로.
   * `persistKey`와 조합 시: **초기 마운트에 저장본이 있으면 fit을 양보**(저장된 줌·스크롤 복원 우선),
   * 저장본이 없거나 이후 pageWidth(단말) 변경 시에는 fit 실행(그 결과가 다시 저장됨).
   */
  fitToWidth?: boolean;
}

/** persistKey 저장 포맷 — z=줌 배율, sx/sy=스크롤 컨테이너 scrollLeft/Top(px). */
type SavedViewport = { z: number; sx: number; sy: number };

function loadSavedViewport(storageKey: string): SavedViewport | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<SavedViewport>;
    if (typeof v.z !== 'number' || typeof v.sx !== 'number' || typeof v.sy !== 'number') return null;
    if (!Number.isFinite(v.z) || !Number.isFinite(v.sx) || !Number.isFinite(v.sy)) return null;
    return { z: v.z, sx: v.sx, sy: v.sy };
  } catch {
    return null; // 파싱/접근 실패 = 무값 취급(중앙 정렬 폴백)
  }
}

/**
 * 줌/패닝/recenter를 캡슐화한 A4 캔버스 뷰포트. 콘텐츠는 children(scaler 안),
 * 편집 오버레이는 overlays render-prop(박스 직속)으로 주입한다.
 */
export function CanvasViewport({
  children,
  overlays,
  wrapCanvas,
  onCanvasClick,
  onCanvasDoubleClick,
  onCanvasContextMenu,
  onCanvasMouseMove,
  onCanvasMouseLeave,
  onOutsideClick,
  remeasureDep,
  onCellDragStart,
  onCellDragMove,
  pageWidth = A4_BASE_WIDTH,
  persistKey,
  fitToWidth = false,
}: CanvasViewportProps) {
  // 캔버스 DOM ref(state) — 마운트/리렌더 후 오버레이가 rect를 다시 측정하도록 트리거한다.
  const [canvasEl, setCanvasEl] = useState<HTMLDivElement | null>(null);
  // template/zoom 변경 후 RenderTemplate DOM이 갱신되면 오버레이를 한 프레임 뒤 재측정하기 위한 tick.
  const [, forceTick] = useState(0);

  // ── A4 캔버스 줌(확대/축소) — Figma 방식. 세션 한정 UI 상태(템플릿에 저장 안 함). ──
  // 콘텐츠(스케일러 div)에만 transform:scale를 걸고, 오버레이(선택/리사이즈/드래그/인라인)는
  // 캔버스(미스케일) 직속으로 두어 getBoundingClientRect 기반 좌표가 그대로 일치하게 한다.
  // (스케일된 r.top-c.top = 논리오프셋*z 이고, 오버레이가 미스케일 공간이라 그 값이 곧 화면 위치.)
  // 리사이즈 포인터 델타·인라인 폰트만 zoom으로 보정한다.
  const [zoom, setZoom] = useState(1);
  const zoomRef = useRef(1);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  // 스크롤 컨테이너(중앙 main) ref + state — 커서/뷰포트 기준 줌 시 scroll 보정에 사용.
  // ref만 쓰면 deps:[]인 패닝/휠 effect가 main=null 시점에 1회 실행 후 재실행되지 않아 리스너가
  // 영구 미등록될 수 있다. 콜백 ref로 state(mainEl)도 갱신해 main 마운트 시 effect가 재실행되게 한다.
  const mainScrollRef = useRef<HTMLElement | null>(null);
  const [mainEl, setMainEl] = useState<HTMLElement | null>(null);
  const setMainRef = useCallback((el: HTMLElement | null) => {
    mainScrollRef.current = el;
    setMainEl(el);
  }, []);
  // 콘텐츠(스케일러)의 원본(미스케일) 크기 — 캔버스 박스를 base*zoom으로 사이징해 스크롤 영역 확보.
  const scalerRef = useRef<HTMLDivElement>(null);
  const [baseSize, setBaseSize] = useState<{ w: number; h: number } | null>(null);
  // 줌 변경 후 앵커(커서 또는 뷰포트 중앙) 콘텐츠 지점을 같은 화면 좌표에 고정하기 위한 보류 정보.
  const zoomAnchorRef = useRef<{
    ux: number;
    uy: number;
    clientX: number;
    clientY: number;
  } | null>(null);

  const clampZoom = useCallback((z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)), []);

  // 콘텐츠 원본 크기 측정(transform 미반영 offset*). template/레이아웃 변화 시 ResizeObserver로 재측정.
  // deps에 canvasEl을 둬, 캔버스가 뒤늦게 마운트될 때 effect가 재실행되게 한다
  // (deps:[]면 scaler=null 시점에 1회 실행 후 재실행 안 돼 baseSize가 영구 null → 박스 width=0 붕괴).
  useLayoutEffect(() => {
    const el = scalerRef.current;
    if (!el) return;
    const measure = () => setBaseSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canvasEl]);

  // 지정 화면 좌표(anchorClientX/Y)를 고정점으로 zoom을 factor배 한다(없으면 뷰포트 중앙).
  const applyZoom = useCallback(
    (factor: number, anchorClientX?: number, anchorClientY?: number) => {
      const main = mainScrollRef.current;
      if (!main || !canvasEl) return;
      const mr = main.getBoundingClientRect();
      const ax = anchorClientX ?? mr.left + mr.width / 2;
      const ay = anchorClientY ?? mr.top + mr.height / 2;
      const z0 = zoomRef.current;
      const z1 = clampZoom(z0 * factor);
      if (z1 === z0) return;
      // 앵커 화면점 아래의 콘텐츠 좌표(미스케일 단위)를 저장 → 커밋 후 같은 화면점에 되돌린다.
      const cr = canvasEl.getBoundingClientRect();
      zoomAnchorRef.current = {
        ux: (ax - cr.left) / z0,
        uy: (ay - cr.top) / z0,
        clientX: ax,
        clientY: ay,
      };
      setZoom(z1);
    },
    [canvasEl, clampZoom],
  );

  const setZoomTo = useCallback(
    (target: number, anchorClientX?: number, anchorClientY?: number) => {
      const z0 = zoomRef.current;
      if (z0 === 0) return;
      applyZoom(clampZoom(target) / z0, anchorClientX, anchorClientY);
    },
    [applyZoom, clampZoom],
  );

  const resetZoom = useCallback(() => setZoomTo(1), [setZoomTo]);

  // 캔버스를 스크롤 영역(=박스 + 오버스크롤 여백) 정중앙으로 스크롤한다. 줌은 건드리지 않음
  // (위치 초기화 버튼 + 마운트 초기 중앙 배치 공용). 여백 덕에 100%에서도 항상 스크롤 공간이 있다.
  const recenterCanvas = useCallback(() => {
    const main = mainScrollRef.current;
    if (!main) return;
    main.scrollLeft = (main.scrollWidth - main.clientWidth) / 2;
    main.scrollTop = (main.scrollHeight - main.clientHeight) / 2;
  }, []);

  // 캔버스를 viewport 중앙에 배치(오버스크롤 여백 때문에 기본 좌상단으로 쏠리지 않게).
  // 미리보기/작성폼 탭에 갔다 양식편집 탭으로 돌아오면 캔버스 div가 *새로 마운트*되는데,
  // 최초 1회 가드만 두면 그땐 recenter가 안 돼 A4가 오버스크롤 여백 좌상단(=우하단으로 밀린 모양)에
  // 남는다. 따라서 *새 canvasEl 인스턴스가 마운트될 때마다* 1회 recenter한다(인스턴스 단위 가드).
  // 같은 뷰 내 zoom/pan으로 옮긴 스크롤 위치는 보존 — canvasEl 동일하면 skip(매 렌더 강제 X).
  // baseSize(박스 높이 측정) + main/canvas 마운트가 모두 갖춰진 뒤, 두 프레임 늦춰 박스 sizing
  // (width/height=base*zoom)·오버스크롤 여백(55vw/vh)이 레이아웃에 반영돼 scrollWidth/Height가
  // 확정된 다음 정렬한다(단일 rAF는 탭 재마운트 시 측정보다 빨라 좌상단에 남는 경우가 있었음).
  // ── 뷰포트(줌/패닝) 유지 — persistKey 지정 시 저장값을 캔버스 인스턴스마다 복원. ──
  // 줌은 여기서 즉시 복원(setZoom), 스크롤은 아래 중앙 정렬 effect가 recenter 대신 적용한다
  // (스크롤 영역이 확정될 때까지 재시도하는 기존 루프를 그대로 재사용 — 복원도 같은 타이밍 문제).
  // 세션 중에는 스크롤/줌 변경이 계속 저장되므로, 탭 복귀 시 복원값 ≈ 직전 위치(상태 싸움 없음).
  const persistStorageKey = persistKey ? `composer-viewport:${persistKey}` : null;
  const savedViewportRef = useRef<SavedViewport | null>(null);
  // 가드는 (canvasEl, key) 쌍 — 캔버스 재마운트뿐 아니라 **키 변경**(새로고침 직후 기본 템플릿
  // 마운트 → 마지막 템플릿 비동기 자동 로드, 또는 템플릿 전환)에도 복원이 다시 실행돼야 한다.
  // canvasEl 단위 가드만 두면 초기 기본 key로 1회 소진돼 실제 템플릿 복원이 영영 스킵된다
  // (2026-07-04 실사고: 새로고침 복원 미동작).
  const lastRestoredRef = useRef<{ el: HTMLDivElement; key: string } | null>(null);
  useEffect(() => {
    if (!persistStorageKey || !canvasEl) return;
    const prev = lastRestoredRef.current;
    if (prev && prev.el === canvasEl && prev.key === persistStorageKey) return;
    lastRestoredRef.current = { el: canvasEl, key: persistStorageKey };
    const saved = loadSavedViewport(persistStorageKey);
    savedViewportRef.current = saved;
    if (!saved) return;
    setZoom(clampZoom(saved.z));
    // 캔버스가 이미 배치된 상태의 키 변경 경로는 여기서 직접 스크롤 적용(중앙 정렬 루프는 캔버스
    // 인스턴스당 1회 창이라 이미 닫혔을 수 있음). 줌 재레이아웃 반영을 위해 두 프레임 재적용.
    // 초기 마운트 경로(스크롤 영역 미생성)는 중앙 정렬 루프가 savedViewportRef로 적용.
    const apply = () => {
      const main = mainScrollRef.current;
      if (!main) return;
      if (main.scrollWidth <= main.clientWidth && main.scrollHeight <= main.clientHeight) return;
      main.scrollLeft = saved.sx;
      main.scrollTop = saved.sy;
    };
    apply();
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      apply();
      raf2 = requestAnimationFrame(apply);
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [persistStorageKey, canvasEl, clampZoom]);

  // 줌·스크롤 변경 시 debounce 저장(300ms). 복원 루프가 유발하는 scroll 이벤트 저장은 무해(동일값).
  const saveTimerRef = useRef<number | undefined>(undefined);
  const scheduleViewportSave = useCallback(() => {
    if (!persistStorageKey) return;
    window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      const main = mainScrollRef.current;
      if (!main) return;
      try {
        localStorage.setItem(
          persistStorageKey,
          JSON.stringify({ z: zoomRef.current, sx: main.scrollLeft, sy: main.scrollTop }),
        );
      } catch {
        // quota/프라이빗 모드 등 저장 실패는 무시 — 복원만 포기(기능 자체는 무영향).
      }
    }, 300);
  }, [persistStorageKey]);
  useEffect(() => {
    if (!persistStorageKey || !mainEl) return;
    mainEl.addEventListener('scroll', scheduleViewportSave, { passive: true });
    return () => {
      window.clearTimeout(saveTimerRef.current);
      mainEl.removeEventListener('scroll', scheduleViewportSave);
    };
  }, [persistStorageKey, mainEl, scheduleViewportSave]);
  useEffect(() => {
    scheduleViewportSave(); // 줌 변경도 저장(스크롤 보정이 없는 순수 줌 변경 커버)
  }, [zoom, scheduleViewportSave]);

  const lastCenteredCanvasRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!mainEl || !canvasEl || !baseSize) return;
    if (lastCenteredCanvasRef.current === canvasEl) return; // 이 인스턴스는 이미 중앙 정렬됨
    const main = mainScrollRef.current;
    if (!main) return;
    // 콘텐츠는 점진 렌더되어 baseSize가 여러 번(예: h 94→224) 갱신되며 effect가 재실행된다.
    // 가드(lastCentered)를 effect 진입 시 설정하면 첫 실행이 예약한 rAF가 곧 cleanup에 취소되고
    // 재실행은 가드로 skip돼 center가 영영 실행되지 않는다(우하단에 남음). 따라서 가드는
    // *center 성공 시점*(여백이 적용돼 스크롤 영역이 생긴 뒤)에 설정한다.
    // 오버스크롤 여백(55vw/vh)·박스 sizing(base*zoom)이 레이아웃에 반영돼 scrollWidth/Height가
    // 확정되는 시점은 브라우저/레이아웃에 따라 수십~수백 ms로 가변이라, 고정 rAF 지연으론
    // 그 전에 실행돼 좌상단(=A4 우하단)에 남는다. 따라서 ResizeObserver로 콘텐츠(여백 포함)
    // 크기 변화를 직접 추종하며, 마운트 초기 짧은 창 동안 변화가 멎을 때까지 매번 재중앙 정렬한다.
    // (초기 창 이후 disconnect — 사용자가 줌/팬으로 옮긴 위치는 건드리지 않는다.)
    let raf = 0;
    const center = () => {
      if (lastCenteredCanvasRef.current === canvasEl) return;
      // 여백 미적용(스크롤 영역 미생성)이면 아직 — 다음 ResizeObserver 발화 때 재시도.
      if (main.scrollWidth <= main.clientWidth && main.scrollHeight <= main.clientHeight) return;
      // 가드는 여기서 걸지 않는다(콘텐츠 높이가 점진 증가하므로 안정될 때까지 매 변화마다 재정렬).
      // persistKey 복원값이 있으면 중앙 정렬 대신 저장된 스크롤을 적용(브라우저가 범위 밖은 clamp).
      const saved = savedViewportRef.current;
      if (saved) {
        main.scrollLeft = saved.sx;
        main.scrollTop = saved.sy;
        return;
      }
      recenterCanvas();
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(center);
    };
    const ro = new ResizeObserver(schedule);
    ro.observe(main);
    if (main.firstElementChild) ro.observe(main.firstElementChild);
    schedule(); // 즉시 1회
    // 콘텐츠 크기가 안정(이 창 동안 변화 멎음)된 뒤 1회 가드 → 이후 재실행은 skip(줌/팬 위치 보존).
    // baseSize가 또 바뀌면 effect 재실행되며 이 타이머가 리셋되어 마지막 변화 기준으로 가드된다.
    const stop = setTimeout(() => {
      ro.disconnect();
      lastCenteredCanvasRef.current = canvasEl;
    }, 600);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(stop);
      ro.disconnect();
    };
  }, [mainEl, canvasEl, baseSize, recenterCanvas]);

  // fit-to-width — fitToWidth 지정 시 마운트 + pageWidth(단말 폭) 변경 때 캔버스 전체가 스크롤 컨테이너
  // 폭에 들어오도록 줌을 자동 설정한다. 상한 1(원본보다 크게는 fit하지 않음). setZoom 후 두 프레임 늦춰
  // recenter — pageWidth 변경 경로는 중앙 정렬 effect의 인스턴스 창(600ms)이 이미 닫혀 있어 여기서 직접
  // 정렬해야 하며, 박스 sizing(width=pageWidth*zoom)이 레이아웃에 반영된 뒤여야 scrollWidth가 확정된다.
  // persistKey 조합 시: 초기 1회는 저장본이 있으면 fit을 양보(복원 effect가 줌·스크롤 적용 — 새로고침 시
  // 스크롤 유지, 2026-07-12 사용자 요청). 저장본 판정은 savedViewportRef가 아닌 localStorage 직접 조회
  // (복원 effect는 canvasEl, 이 effect는 mainEl 마운트 트리거라 실행 순서 비보장 — ref 레이스 회피).
  const initialFitDecidedRef = useRef(false);
  useEffect(() => {
    if (!fitToWidth || !mainEl) return;
    if (!initialFitDecidedRef.current) {
      initialFitDecidedRef.current = true;
      if (persistStorageKey && loadSavedViewport(persistStorageKey)) return; // 복원 우선
    }
    const avail = mainEl.clientWidth - 24; // 좌우 여백 약 24px 확보
    if (avail <= 0) return;
    setZoom(clampZoom(Math.min(1, avail / pageWidth)));
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => recenterCanvas());
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [fitToWidth, pageWidth, mainEl, clampZoom, recenterCanvas, persistStorageKey]);

  // 줌 변경 커밋(DOM 반영) 후, 앵커 콘텐츠 지점을 같은 화면 좌표에 고정하도록 scroll 보정.
  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current;
    const main = mainScrollRef.current;
    if (!anchor || !main || !canvasEl) return;
    zoomAnchorRef.current = null;
    const cr = canvasEl.getBoundingClientRect();
    const curX = cr.left + anchor.ux * zoom;
    const curY = cr.top + anchor.uy * zoom;
    main.scrollLeft += curX - anchor.clientX;
    main.scrollTop += curY - anchor.clientY;
  }, [zoom, canvasEl]);

  // template/zoom 변경 후 RenderTemplate DOM이 갱신되면(레이아웃·스케일 반영) 오버레이를 한 프레임 뒤 재측정.
  useEffect(() => {
    const raf = requestAnimationFrame(() => forceTick((n) => n + 1));
    return () => cancelAnimationFrame(raf);
  }, [zoom, remeasureDep]);

  // Ctrl+휠 → 커서 기준 줌(브라우저 페이지 줌 가로채기). preventDefault 위해 passive:false native 등록.
  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      // 휠 1노치(deltaY≈100)당 약 10%.
      const factor = Math.pow(1.1, -e.deltaY / 100);
      applyZoom(factor, e.clientX, e.clientY);
    };
    main.addEventListener('wheel', onWheel, { passive: false });
    return () => main.removeEventListener('wheel', onWheel);
  }, [applyZoom, mainEl]);

  // Ctrl + =/-/0 키보드 줌(입력/텍스트 영역 포커스 시 제외).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        applyZoom(1.2);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        applyZoom(1 / 1.2);
      } else if (e.key === '0') {
        e.preventDefault();
        resetZoom();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applyZoom, resetZoom]);

  // 캔버스 패닝 — 스페이스+드래그 / 중간버튼(휠클릭) 드래그로 스크롤 컨테이너 이동.
  // 줌과 독립(스크롤만 이동, scale 무관). 패닝은 세션 UI 동작이라 상태 저장 X.
  // panState는 커서 표시용(armed=grab, panning=grabbing). 실제 드래그 로직은 ref로 처리(리렌더 최소).
  const [panState, setPanState] = useState<'idle' | 'armed' | 'panning'>('idle');
  const panRef = useRef<{ armed: boolean; panning: boolean; x: number; y: number }>({
    armed: false,
    panning: false,
    x: 0,
    y: 0,
  });
  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;

    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };

    const onMove = (e: MouseEvent) => {
      if (!panRef.current.panning) return;
      const dx = e.clientX - panRef.current.x;
      const dy = e.clientY - panRef.current.y;
      panRef.current.x = e.clientX;
      panRef.current.y = e.clientY;
      // 드래그 방향대로 콘텐츠가 따라오도록 스크롤은 반대로 이동.
      main.scrollLeft -= dx;
      main.scrollTop -= dy;
    };

    const onUp = () => {
      if (!panRef.current.panning) return;
      panRef.current.panning = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // 드래그 종료 후 스페이스가 여전히 눌려 있으면 grab 유지, 아니면 idle.
      setPanState(panRef.current.armed ? 'armed' : 'idle');
      // 패닝 직후 발생하는 click이 캔버스 선택을 해제하지 않도록 1회 흡수(capture).
      const swallow = (ev: MouseEvent) => {
        ev.stopPropagation();
        main.removeEventListener('click', swallow, true);
      };
      main.addEventListener('click', swallow, true);
      setTimeout(() => main.removeEventListener('click', swallow, true), 0);
    };

    const beginPan = (clientX: number, clientY: number) => {
      panRef.current.panning = true;
      panRef.current.x = clientX;
      panRef.current.y = clientY;
      setPanState('panning');
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 1) {
        // 중간버튼(휠클릭) — 브라우저 auto-scroll 방지 후 패닝.
        e.preventDefault();
        beginPan(e.clientX, e.clientY);
      } else if (e.button === 0 && panRef.current.armed) {
        // 스페이스 패닝 모드 중 좌클릭 — 노드 선택/마퀴/DnD 억제.
        e.preventDefault();
        beginPan(e.clientX, e.clientY);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      // 입력 필드(textarea/input/contenteditable) 포커스 중이면 공백 입력 보존(패닝 비활성).
      if (isTypingTarget(e.target)) return;
      e.preventDefault(); // 스페이스 페이지 스크롤/버튼 활성 방지(autorepeat 포함).
      if (panRef.current.armed) return;
      panRef.current.armed = true;
      if (!panRef.current.panning) setPanState('armed');
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' && e.key !== ' ') return;
      panRef.current.armed = false;
      if (!panRef.current.panning) setPanState('idle');
    };

    main.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      main.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // mainEl: main이 뒤늦게 마운트될 때 리스너를 등록하도록 재실행 트리거.
  }, [mainEl]);

  // ── 셀 드래그 범위 선택(엑셀/HWP식) ──
  // 셀(`[data-composer-cell]`) 위 plain mousedown → threshold(4px) 초과 이동부터 드래그로 보고,
  // anchor~현재 셀 직사각 범위를 실시간 선택한다. threshold 미만이면 드래그로 보지 않아 기존 단일
  // 클릭/더블클릭(인라인 편집) 동작이 그대로 보존된다. 드래그가 일어났으면 직후 click을 1회 흡수해
  // (capture) handleCanvasClick의 단일 선택이 범위를 덮어쓰지 않게 한다.
  const cellDragRef = useRef<{
    anchor: CellRef | null;
    startX: number;
    startY: number;
    dragging: boolean;
  }>({ anchor: null, startX: 0, startY: 0, dragging: false });
  // 콜백 최신값 ref — effect는 [mainEl]에만 의존(콜백 변경 시 재등록하지 않음).
  const onCellDragStartRef = useRef(onCellDragStart);
  const onCellDragMoveRef = useRef(onCellDragMove);
  useEffect(() => {
    onCellDragStartRef.current = onCellDragStart;
    onCellDragMoveRef.current = onCellDragMove;
  }, [onCellDragStart, onCellDragMove]);

  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;
    const DRAG_THRESHOLD = 4;

    // 커서 위치(clientX/Y) 아래의 셀 CellRef(없으면 null — 셀 밖/다른 오버레이 strip 위).
    const cellRefAt = (clientX: number, clientY: number): CellRef | null => {
      const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
      const cellEl = el?.closest<HTMLElement>('[data-composer-cell]');
      return cellEl ? parseCellRef(cellEl.dataset.composerCell ?? '') : null;
    };

    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };

    const onMove = (e: MouseEvent) => {
      const st = cellDragRef.current;
      if (!st.anchor) return;
      if (!st.dragging) {
        // threshold 넘기 전엔 드래그로 보지 않음(단일 클릭/더블클릭 보존).
        if (
          Math.abs(e.clientX - st.startX) < DRAG_THRESHOLD &&
          Math.abs(e.clientY - st.startY) < DRAG_THRESHOLD
        ) {
          return;
        }
        st.dragging = true;
        onCellDragStartRef.current?.(st.anchor); // 드래그 시작 = anchor 셀 단일 선택.
      }
      const target = cellRefAt(e.clientX, e.clientY);
      // 같은 표의 셀 위일 때만 범위 갱신(다른 표/셀 밖/strip 위면 직전 범위 유지).
      if (!target || target.tableId !== st.anchor.tableId) return;
      onCellDragMoveRef.current?.(st.anchor, target);
    };

    const onUp = () => {
      const wasDragging = cellDragRef.current.dragging;
      cellDragRef.current.anchor = null;
      cellDragRef.current.dragging = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (!wasDragging) return;
      // 드래그 직후 발생하는 click이 단일 선택으로 범위를 덮어쓰지 않도록 1회 흡수(capture).
      const swallow = (ev: MouseEvent) => {
        ev.stopPropagation();
        main.removeEventListener('click', swallow, true);
      };
      main.addEventListener('click', swallow, true);
      setTimeout(() => main.removeEventListener('click', swallow, true), 0);
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return; // 좌클릭만(중간버튼=패닝).
      if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return; // 범위/토글은 click 핸들러가 처리.
      if (panRef.current.armed || panRef.current.panning) return; // 패닝 우선.
      if (isTypingTarget(e.target)) return; // 인라인 편집 textarea 내부 = 텍스트 선택.
      const el = e.target as HTMLElement | null;
      const cellEl = el?.closest<HTMLElement>('[data-composer-cell]');
      if (!cellEl) return; // 셀 위에서 시작한 드래그만(노드/배경 시작은 무시).
      const anchor = parseCellRef(cellEl.dataset.composerCell ?? '');
      if (!anchor) return;
      cellDragRef.current = { anchor, startX: e.clientX, startY: e.clientY, dragging: false };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    main.addEventListener('mousedown', onMouseDown);
    return () => {
      main.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // mainEl: main이 뒤늦게 마운트될 때 리스너 재등록.
  }, [mainEl]);

  const wrapBox = wrapCanvas ?? ((box: ReactNode) => box);

  return (
    <div className="flex-1 min-w-0 min-h-0 relative flex">
      <main
        ref={setMainRef}
        className="flex-1 min-w-0 bg-gray-200 rounded-card overflow-auto"
        // 패닝 커서 — armed(스페이스 대기)=grab, panning(드래그 중)=grabbing. user-select는 항상
        // none: 디자인 캔버스(Figma식)라 셀 Shift-클릭 범위 선택 시 네이티브 텍스트 선택이 끼지 않게.
        style={{
          cursor: panState === 'panning' ? 'grabbing' : panState === 'armed' ? 'grab' : undefined,
          userSelect: 'none',
        }}
        onClick={(e) => {
          // A4 흰 박스 바깥(회색 배경) 클릭 → 외부 클릭 콜백(form: 선택 해제). 박스 내부 클릭은
          // onCanvasClick이 처리하므로 canvasEl.contains로 중복 실행 방지.
          if (canvasEl && !canvasEl.contains(e.target as Node)) {
            onOutsideClick?.();
          }
        }}
      >
        {/* 오버스크롤 여백 — 박스 사방에 viewport 비례 여백을 둬 스크롤 콘텐츠를 항상 viewport보다
            크게 만든다(100% 줌에서도 패닝 가능, Figma식). 여백은 이 바깥 래퍼에만 주므로 A4 박스
            (relative)·scaler·오버레이의 상대 좌표계는 불변. mx-auto는 여백 안에서 박스를 가로 중앙에
            둔다(여백으로 항상 overflow되면 마운트 후 recenterCanvas가 초기 위치를 중앙으로 보정).

            ⚠️ 이 여백은 **오버레이가 쓰는 자리이기도 하다** — 분기 슬롯 탭 바(BranchSlotTabsOverlay)는
            자기 노드 **바로 위**에 뜨므로, 문서 맨 위 branch의 바는 박스 위쪽 바깥(음수 y)에 그려진다.
            그게 보이는 건 이 래퍼와 A4 박스에 overflow가 없어(clip은 스크롤 컨테이너 main 하나뿐,
            그마저 여백 바깥) 음수 좌표가 이 padding 안에 그려지기 때문이다. 둘 중 어디에든
            overflow(hidden/clip/auto)를 주면 **에러 없이 그 바만 사라진다.** */}
        <div className="mx-auto w-fit" style={{ padding: '55vh 55vw' }}>
          {wrapBox(
            // A4 느낌 흰 박스 — 클릭 위임으로 노드 선택. 오버레이 기준 컨테이너(position:relative).
            // 줌: 콘텐츠는 내부 스케일러(transform:scale)로 확대, 박스는 base*zoom으로 사이징해
            // 스크롤 영역을 확보한다. 오버레이는 이 박스(미스케일) 직속이라 좌표가 그대로 일치.
            <div
              ref={setCanvasEl}
              onClick={onCanvasClick}
              onDoubleClick={onCanvasDoubleClick}
              onContextMenu={onCanvasContextMenu}
              onMouseMove={onCanvasMouseMove}
              onMouseLeave={onCanvasMouseLeave}
              className="shadow-lg bg-white"
              style={{
                position: 'relative',
                // 폭은 측정 없이 페이지 원본폭(pageWidth, 방향 반영)*zoom으로 확정(scaler 측정과의 순환
                // 의존 차단 + 초기 0폭 붕괴 방지).
                // 높이만 콘텐츠 측정(baseSize.h)*zoom으로 — 미측정 시 √2 비율로 임시 사이징해 0 붕괴를 막는다.
                width: pageWidth * zoom,
                height: (baseSize ? baseSize.h : Math.round(pageWidth * 1.414)) * zoom,
              }}
            >
              {/* 스케일러 — 콘텐츠(양식)만 scale. 오버레이는 바깥(미스케일)에 둔다.
                  폭을 페이지 원본폭(pageWidth, 방향 반영)으로 명시 — RenderTemplate 폭과 동일하며, 박스 폭
                  (containing block)에 의존하던 fit-content 측정의 순환 의존을 제거해 offsetHeight 측정을
                  결정적으로 만든다. */}
              <div
                ref={scalerRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: pageWidth,
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top left',
                }}
              >
                {children}
              </div>
              {/* 편집 오버레이(선택/배지/드래그/리사이즈/인라인) — 박스(미스케일) 직속. */}
              {canvasEl && overlays?.(canvasEl, zoom)}
            </div>,
          )}
        </div>
      </main>
      {/* 우하단 줌 컨트롤 — 스크롤과 무관하게 고정(상위 relative 기준 absolute). */}
      <div
        className="absolute bottom-4 right-4 z-30 flex items-center gap-1 rounded-card border border-gray-300 bg-white/95 px-2 py-1 shadow-lg backdrop-blur"
        role="group"
        aria-label="캔버스 확대/축소"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => applyZoom(1 / 1.2)}
          disabled={zoom <= ZOOM_MIN + 1e-6}
          title="축소 (Ctrl -)"
          aria-label="축소"
          className="w-6 h-6 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
        >
          −
        </button>
        <input
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoomTo(Number(e.target.value))}
          aria-label="확대/축소 비율"
          className="w-24 accent-brand-600"
        />
        <button
          type="button"
          onClick={() => applyZoom(1.2)}
          disabled={zoom >= ZOOM_MAX - 1e-6}
          title="확대 (Ctrl +)"
          aria-label="확대"
          className="w-6 h-6 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
        >
          +
        </button>
        <button
          type="button"
          onClick={resetZoom}
          title="100%로 재설정 (Ctrl 0)"
          aria-label="100%로 재설정"
          className="min-w-[3rem] px-1 text-xs tabular-nums text-gray-700 hover:bg-gray-100 rounded"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          type="button"
          onClick={recenterCanvas}
          title="위치 초기화 (가운데로)"
          aria-label="위치 초기화"
          className="w-6 h-6 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100"
        >
          {/* 크로스헤어(가운데로) 아이콘 */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="7" />
            <line x1="12" y1="1.5" x2="12" y2="5" />
            <line x1="12" y1="19" x2="12" y2="22.5" />
            <line x1="1.5" y1="12" x2="5" y2="12" />
            <line x1="19" y1="12" x2="22.5" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
